export const stripNdcDashes = (
  ndc: string | null | undefined,
): string => {
  if (ndc == null) {
    return "";
  }
  return ndc.replace(/[-\s]/g, "").trim();
};

export const countNdcDigits = (ndc: string | null | undefined): number => {
  if (ndc == null || typeof ndc !== "string") {
    return 0;
  }
  return ndc.replace(/\D/g, "").length;
};

const NON_DIGIT_GLOBAL = /\D/g;

export const clampNdcInputElevenDigits = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 5) {
    return digits;
  }
  if (digits.length <= 9) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return `${digits.slice(0, 5)}-${digits.slice(5, 9)}-${digits.slice(9)}`;
};

export const applyNdcInputChange = (
  previous: string,
  next: string,
  options?: { lockDashes?: boolean }
): string => {
  const prevDigits = stripNdcDashes(previous).replace(NON_DIGIT_GLOBAL, "");
  const nextDigits = stripNdcDashes(next)
    .replace(NON_DIGIT_GLOBAL, "")
    .slice(0, 11);

  if (
    options?.lockDashes &&
    previous.includes("-") &&
    prevDigits.length > 0 &&
    prevDigits === nextDigits
  ) {
    return clampNdcInputElevenDigits(nextDigits);
  }

  return next;
};

const trimFirstAndLastChars = (ndc: string) => {
  const ndcLength = ndc.length;
  return ndc.substring(1, ndcLength - 1);
};

export const formatNdc = (ndc: string): string => {
  const noHyphensNdc = stripNdcDashes(ndc);
  const ndcLength = noHyphensNdc.length;
  if (ndcLength > 11) {
    return trimFirstAndLastChars(noHyphensNdc);
  }
  return noHyphensNdc;
};

export const isNdcValid = (ndc: string) => {
  if (!ndc || typeof ndc !== "string") return false;

  const formattedNdc = formatNdc(ndc);
  const formattedNdcLength = formattedNdc.length;

  if (formattedNdcLength < 9 || formattedNdcLength > 11) return false;
  return true;
};

/** True when NDC is complete enough for a drug lookup (10- or 11-digit). */
export const isNdcReadyForDrugLookup = (ndc: string) => {
  if (!isNdcValid(ndc)) return false;
  const len = formatNdc(ndc).length;
  return len === 10 || len === 11;
};

/** GS1/QR streams (prefix 01 or length > 15) complete on Enter. */
export const isGs1StreamInProgress = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length > 15) return true;
  return trimmed.replace(/-/g, "").startsWith("01");
};

/** Arm the barcode idle timer only for short numeric wedge scans. */
export const shouldArmBarcodeScanTimer = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (isGs1StreamInProgress(trimmed)) return false;

  const dashless = trimmed.replace(/-/g, "");
  return /^\d+$/.test(dashless);
};
